import { BriefDeliverableStatusEnum } from "@entities/brief/lib/constants";
import { useAddScriptToTask } from "@entities/brief/lib/hooks/scripting/useAddScriptToTask";
import { useGetBriefScripting } from "@entities/brief/lib/hooks/scripting/useGetScripting";
import { useGetMe } from "@entities/Onboarding/lib/hooks/useGetMe";
import { ICampaignDeliverableTaskForm } from "@shared/lib/types/briefCampains";
import { Button } from "@shared/ui";
import CampaignScriptingDrawer from "@widgets/CampaignScriptingDrawer";
import dayjs from "dayjs";
import { AlertCircle, ArrowRight, CheckCircle2, Clock, Plus, XCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import styles from "./styles.module.scss";

interface AddCampaignScriptProps {
  campaignInfluencerId: string;
  deliverableConfigRefId: string;
  taskId: string;
  taskForm?: ICampaignDeliverableTaskForm;
  taskStatus: BriefDeliverableStatusEnum;
  refetchInfluencer: () => void;
  scriptingId: string;
  briefId: string;
}

type ScriptStatus =
  | "pending"
  | "in_progress"
  | "in_review"
  | "needs_approval"
  | "changes_requested"
  | "completed";

export default function AddCampaignScript({
  campaignInfluencerId,
  deliverableConfigRefId,
  taskId,
  taskForm,
  taskStatus,
  refetchInfluencer,
  scriptingId,
  briefId,
}: AddCampaignScriptProps) {
  const { addScriptToTask, isCreating, isCreated } =
    useAddScriptToTask(briefId);
  const [isScriptDrawerOpen, setIsScriptDrawerOpen] = useState(false);

  const { userDetails } = useGetMe();
  // const isAddScriptDisabled =
  //   userDetails?.userEntity?.permissions.includes("super-brand-org") ||
  //   userDetails?.userEntity?.permissions.includes("super")
  //     ? false
  //     : !userDetails?.userEntity?.permissions.includes("modify-briefs-org");

  const { scripting } = useGetBriefScripting(scriptingId);

  const latestUnresolvedComment = useMemo(
    () =>
      scripting?.script?.comments?.filter((comment) => !comment.isResolved) ||
      [],
    [scripting?.script?.comments]
  );

  const hasScriptId = !!scriptingId;

  const scriptStatus: ScriptStatus = useMemo(() => {
    // If no script ID at all, show pending/empty state
    if (!hasScriptId) {
      return "pending";
    }

    // If script ID exists but no scripting data yet, or status is pending, show in progress
    if (hasScriptId && taskStatus === BriefDeliverableStatusEnum.Pending) {
      return "in_progress";
    }

    switch (taskStatus) {
      case BriefDeliverableStatusEnum.PendingApproval:
        return "needs_approval";
      case BriefDeliverableStatusEnum.EditsRequested:
        return "changes_requested";
      case BriefDeliverableStatusEnum.Approved:
      case BriefDeliverableStatusEnum.Completed:
        return "completed";
      case BriefDeliverableStatusEnum.Uploaded:
        return "in_review";
      default:
        return "in_progress";
    }
  }, [hasScriptId, taskStatus]);

  const handleCreateScript = () => {
    addScriptToTask({
      campaignInfluencerId,
      deliverableConfigRefId,
      taskId,
    });
  };

  const handleViewScript = () => {
    if (scriptingId) {
      // window.open(
      //   ROUTES.BRIEF_V2_SCRIPT.replace(":scriptId", taskForm.scriptingId),
      //   "_blank"
      // );
      setIsScriptDrawerOpen(true);
    }
  };

  useEffect(() => {
    if (isCreated) {
      setIsScriptDrawerOpen(true);

      refetchInfluencer();
      // Open script page in new window after creation
      // window.open(
      //   ROUTES.BRIEF_V2_SCRIPT.replace(":scriptId", taskForm.scriptingId),
      //   "_blank"
      // );
    }
  }, [isCreated]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    const date = dayjs(dateStr);
    return date.format("MMM DD, h:mm A");
  };

  const getUploadedDate = () => {
    if (scripting?.script?.uploadedScript?.generatedAi?.createdAt) {
      return formatDate(scripting.script.uploadedScript.generatedAi.createdAt);
    }
    if (scripting?.script?.createdAt) {
      return formatDate(scripting.script.createdAt);
    }
    return "";
  };

  const renderCommentsPreview = () => {
    if (latestUnresolvedComment.length === 0) return null;

    return (
      <div className={styles.commentsPreview}>
        <div className={styles.commentsTitle}>Latest Feedback</div>
        <div className={styles.commentsList}>
          {latestUnresolvedComment.map((comment) => (
            <div key={comment._id} className={styles.commentItem}>
              <div className={styles.commentBar}></div>
              <div className={styles.commentContent}>
                <span className={styles.commentUser}>
                  {comment.createdBy?.firstName} {comment.createdBy?.lastName}
                  :{" "}
                </span>
                <span className={styles.commentText}>{comment.comment}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (scriptStatus === "pending") {
      return (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <Clock size={18} />
          </div>
          <p className={styles.emptyTextTitle}>Script pending</p>
          <p className={styles.emptyTextDescription}>
            Script is still pending from the creator. You will be able to review
            it once it is submitted.
          </p>
            {/* <DisabledPopover isDisabled={isAddScriptDisabled}> */}
              <Button
                type="primary"
                // disabled={isAddScriptDisabled}
                icon={<Plus size={14} />}
                onClick={handleCreateScript}
                loading={isCreating}
                className={styles.createButton}
              >
                Create Script
              </Button>
            {/* </DisabledPopover> */}
        </div>
      );
    }

    if (scriptStatus === "in_progress") {
      return (
        <div className={styles.statusContainer}>
          {renderCommentsPreview()}
          <div className={styles.statusFooter}>
            {/* <span className={styles.statusText}>In Progress</span> */}

            <Button
              type="link"
              icon={<ArrowRight size={12} />}
              onClick={handleViewScript}
              className={styles.viewButton}
            >
              View Script
            </Button>
          </div>
        </div>
      );
    }

    if (scriptStatus === "in_review") {
      return (
        <div className={styles.statusContainer}>
          <div className={styles.statusBanner} data-status="in_review">
            <Clock size={16} className={styles.statusIcon} />
            <div>
              <div className={styles.statusTitle}>In Internal Review</div>
              <p className={styles.statusDescription}>
                This script is currently under internal review. You can override
                and approve or request changes directly.
              </p>
            </div>
          </div>
          {renderCommentsPreview()}
          <div className={styles.statusFooter}>
            <span className={styles.dateText}>
              {getUploadedDate() ? `Uploaded on ${getUploadedDate()}` : ""}
            </span>
            <Button
              type="link"
              icon={<ArrowRight size={12} />}
              onClick={handleViewScript}
              className={styles.viewButton}
            >
              View Script
            </Button>
          </div>
        </div>
      );
    }

    if (scriptStatus === "needs_approval") {
      return (
        <div className={styles.statusContainer}>
          <div className={styles.statusBanner} data-status="needs_approval">
            <AlertCircle size={16} className={styles.statusIcon} />
            <div>
              <div className={styles.statusTitle}>Awaiting Brand Review</div>
              <p className={styles.statusDescription}>
                Please review the script and provide feedback or approve it to
                proceed.
              </p>
            </div>
          </div>
          {renderCommentsPreview()}
          <div className={styles.statusFooter}>
            <span className={styles.dateText}>
              {getUploadedDate() ? `Uploaded on ${getUploadedDate()}` : ""}
            </span>
            <Button
              type="primary"
              onClick={handleViewScript}
              className={styles.reviewButton}
            >
              Review Script
            </Button>
          </div>
        </div>
      );
    }

    if (scriptStatus === "changes_requested") {
      return (
        <div className={styles.statusContainer}>
          <div className={styles.statusBanner} data-status="changes_requested">
            <XCircle size={16} className={styles.statusIcon} />
            <div>
              <div className={styles.statusTitle}>Changes Requested</div>
              <p className={styles.statusDescription}>
                Feedback sent to creator. Waiting for revised version.
              </p>
            </div>
          </div>

          {renderCommentsPreview()}

          <div className={styles.statusFooter}>
            <Button
              type="link"
              onClick={handleViewScript}
              className={styles.viewDetailsButton}
            >
              View Details
            </Button>
          </div>
        </div>
      );
    }

    if (scriptStatus === "completed") {
      const approvedItem = [
        ...(scripting?.campaignInfluencerTaskState?.timeline || []),
      ]
        .reverse()
        ?.find((item) => item.status === BriefDeliverableStatusEnum.Approved);

      return (
        <div className={styles.statusContainer}>
          <div className={styles.statusBanner} data-status="completed">
            <CheckCircle2 size={14} className={styles.statusIcon} />
            <span className={styles.statusTitle}>Script Approved</span>
          </div>
          <div className={styles.completedInfo}>
            <div>
              Uploaded:{" "}
              <span className={styles.infoValue}>
                {getUploadedDate()
                  ? dayjs(scripting?.script?.createdAt).format("MMM DD")
                  : ""}
              </span>
            </div>

            {approvedItem && (
              <div>
                Approved:{" "}
                <span className={styles.infoValue}>
                  {approvedItem?.createdAt
                    ? `${dayjs(approvedItem.createdAt).format("MMM DD")} by ${
                        approvedItem.createdBy?.firstName || ""
                      }`
                    : ""}
                </span>
              </div>
            )}
          </div>
          <Button
            block
            onClick={handleViewScript}
            className={styles.viewApprovedButton}
          >
            View Script
          </Button>
        </div>
      );
    }

    return null;
  };

  return (
    <div className={styles.container}>
      {renderContent()}
      <CampaignScriptingDrawer
        open={isScriptDrawerOpen}
        onClose={() => setIsScriptDrawerOpen(false)}
        scriptingId={scriptingId}
        refetchInfluencer={refetchInfluencer}
      />
    </div>
  );
}
